/** eslint-disable no-undef */
import {Component, Host, Prop, h, Element, forceUpdate, State, Fragment} from "@stencil/core";
import {store} from "../stores/notification-handler-store";
import {store as userSettingsStore} from "../stores/user-settings-handler-store";
import {AuthSubscribe, AuthUnsubscribe} from "../auth/auth";
import {translations} from "./i18n";
import {i18nProvider} from "@trumpf/ux.i18n";
import {Body, Header, Loading} from "./notification-center-sub-components";

const settingsENV = {
	dev: "https://trumpf.com/go/notification/settings/",
	qa: "https://trumpf.com/go/notification/settings/",
	prod: "https://trumpf.com/go/notification/settings/",
} as const;

/**
 * Notification center component to display new notifications.
 */
@Component({
	tag: "uxn-notification-center",
	styleUrl: "notification-center.scss",
	shadow: false,
})
export class NotificationCenter {
	#baseClass: string = "uxn-notification-center";

	@Element() el: HTMLUxnNotificationCenterElement;

	/** the url to the main notification center */
	@Prop() notificationsCenterUrl: string = "";
	/** the url to the notification setting */
	@Prop() notificationCenterTarget: string = "_blank";
	/** the headline of the notifications layer */
	@Prop() notificationSettingsUrl: string = "";
	/** target of the notifications setting link */
	@Prop() notificationSettingsTarget: string = "";
	/** target of the notifications setting link */
	@Prop() settingsDisabled: boolean = false;
	/** backend api version */
	@Prop() version: string = "2.0";
	/** environment for fetching data */
	@Prop() env: string = "prod";
	/** language code */
	@Prop() locale: string = "de_DE";
	/** debugging */
	@Prop() debug: boolean = false;

	@State() retry: number = 0;
	@State() settingsURL: string;

	#authSubscription: number | null = null;
	#i18nSubscriberId: string;

	#customTranslation() {
		const {i18n} = i18nProvider;

		Object.entries(translations).forEach(([language, bundle]) => i18n.addResourceBundle(language, "notifications", bundle));

		/**
		 * Handles language changes in the i18n instance, and falls back to a
		 * related language if the exact language isn't available in the translations.
		 */
		i18n.on("languageChanged", (lng) => {
			if (Object.keys(translations).includes(lng)) return;

			const lngCode = lng.split("_")[0];
			const fallbackLng = Object.keys(translations).find((key) => key.startsWith(lngCode));
			if (fallbackLng) i18n.changeLanguage(fallbackLng);
		});
	}

	componentWillLoad() {
		this.settingsURL = this.notificationSettingsUrl || settingsENV[this.env];
	}

	async componentDidLoad() {
		this.#authSubscription = await AuthSubscribe(this.version, this.env, this.locale, this.debug);
		this.#customTranslation();
		await i18nProvider.changeLanguage(this.locale || "de_DE");

		this.#i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
		store.onChange("notifications", () => {
			this.#resetInfiniteScroll();
		});
	}

	#resetInfiniteScroll = (): void => {
		const infiniteScrollSensor = this.el.querySelector<any>("ux-infinite-scroll-sensor");
		if (infiniteScrollSensor === null) return;
		infiniteScrollSensor.hasFired = false;
	};

	disconnectedCallback() {
		if (this.#authSubscription !== null) {
			AuthUnsubscribe(this.#authSubscription);
			this.#authSubscription = null; // Clear the subscription ID after unsubscribing
		}
		i18nProvider.unsubscribe(this.#i18nSubscriberId);
		store.set("token", null);
	}

	render() {
		const isLoading = userSettingsStore.get("loading") || store.get("loading");
		// eslint-disable-next-line @typescript-eslint/no-unsafe-return
		return (
			<Host class={this.#baseClass}>
				{isLoading ? (
					<Loading />
				) : (
					<Fragment>
						<Header settingsURL={this.settingsURL} settingsDisabled={this.settingsDisabled} notificationSettingsTarget={this.notificationSettingsTarget} />
						<Body />
					</Fragment>
				)}
			</Host>
		);
	}
}
